import api from "../utils/api";
import { LOGOUT } from "./types";
import { send as sendEmail } from "./email.template";
import { normalizeCalendarDescription } from "../utils/calendarDescription";

export const US_IANA_TIMEZONES = [
    // Eastern
    "America/New_York",
    "America/Detroit",
    "America/Kentucky/Louisville",
    "America/Kentucky/Monticello",
    "America/Indiana/Indianapolis",
    "America/Indiana/Vincennes",
    "America/Indiana/Winamac",
    "America/Indiana/Marengo",
    "America/Indiana/Petersburg",
    "America/Indiana/Vevay",

    // Central
    "America/Chicago",
    "America/Indiana/Tell_City",
    "America/Menominee",
    "America/North_Dakota/Center",
    "America/North_Dakota/New_Salem",
    "America/North_Dakota/Beulah",

    // Mountain
    "America/Denver",
    "America/Boise",

    // Mountain (no DST)
    "America/Phoenix",

    // Pacific
    "America/Los_Angeles",

    // Alaska
    "America/Juneau",
    "America/Sitka",
    "America/Metlakatla",
    "America/Yakutat",
    "America/Anchorage",
    "America/Nome",

    // Aleutian
    "America/Adak",

    // Hawaii
    "Pacific/Honolulu",

    // US territories (optional but commonly treated as “USA” org-wise)
    "America/Puerto_Rico",
    "Pacific/Guam",
    "Pacific/Saipan",
    "Pacific/Pago_Pago",
];

export function formatUtcIsoInTimeZone(isoUtc, timeZone) {
    if (!isoUtc) return "";

    const d = isoUtc instanceof Date ? isoUtc : new Date(isoUtc);
    if (Number.isNaN(d.getTime())) return "";

    // NOTE: this assumes the incoming value is UTC (e.g., ends with "Z" or is UTC-based).
    return new Intl.DateTimeFormat("en-US", {
        timeZone,
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    }).format(d);
}

export function mapApiEventToUi(ev) {
    if (!ev) return null;

    const sourceUuid = ev.db?.sourceUuid ?? ev.sourceUuid ?? ev.uuid ?? ev.id ?? null;
    const uuid = ev.db?.uuid ?? ev.uuid ?? ev.id ?? null;

    const out = {
        uuid,
        sourceUuid,
        status: ev.status ?? null,
        style: ev.style ?? null,
        eventType: ev.db?.eventType ?? ev.eventType ?? null,
        title: ev.title ?? ev.summary ?? ev.name ?? "",
        description: normalizeCalendarDescription(ev.description),
        allDay: !!(ev.allDay ?? ev.all_day ?? false),
        startsAt: ev.startsAt ?? ev.startAt ?? ev.start ?? null,
        endsAt: ev.endsAt ?? ev.endAt ?? ev.end ?? null,
        location: ev.location ?? "",
        recurrence: ev.recurrence ?? ev.rrule ?? "",
        ics: ev.ics ?? "",
        attendees: [],
        calendar: null,
        createdBy: null,
        case: null,
        person: null,
        timezones: []
    };

    // ---- calendar (prefer db.calendar, then source Calendar include) ----
    const cal =
        ev.db?.calendar ??
        ev.Calendar ?? // if you exposed it on merge
        ev.calendar ??
        null;

    if (cal) {
        out.calendar = {
            uuid: cal.uuid ?? ev.db?.calendarUuid ?? null,
            name: cal.name ?? "",
            role: cal.role ?? "unknown",
        };
    } else if (ev.db?.calendarUuid) {
        out.calendar = { uuid: ev.db.calendarUuid, name: "", role: "unknown" };
    }

    // ---- createdBy (prefer source CreatedBy include, then db.createdByUuid only) ----
    const createdBy =
        ev.CreatedBy ??
        ev.createdBy ??
        ev.db?.createdBy ??
        null;

    if (createdBy) {
        out.createdBy = {
            uuid: createdBy.uuid ?? ev.db?.createdByUuid ?? null,
            displayName: createdBy.displayName ?? "",
            firstName: createdBy.firstName ?? "",
            lastName: createdBy.lastName ?? "",
            email: createdBy.email ?? "",
            phone: createdBy.phone ?? "",
            userUuid: createdBy.userUuid ?? null,
        };
    } else if (ev.db?.createdByUuid) {
        out.createdBy = { uuid: ev.db.createdByUuid };
    }

    // ---- case (prefer db.case, then source Case include) ----
    const kase =
        ev.db?.case ??
        ev.Case ??
        ev.case ??
        null;

    if (kase) {
        out.case = {
            caption: kase.caption ?? "",
            number: kase.number ?? kase.caseNumber ?? "",
            status: kase.status ?? "",
            uuid: kase.uuid ?? ev.db?.caseUuid ?? null,
        };
    } else if (ev.db?.caseUuid) {
        out.case = { uuid: ev.db.caseUuid };
    }

    // ---- person (prefer db.person, then source Person include) ----
    const person =
        ev.db?.person ??
        ev.Person ??
        ev.person ??
        null;

    if (person) {
        out.person = {
            displayName: person.displayName ?? "",
            email: person.email ?? "",
            firstName: person.firstName ?? "",
            lastName: person.lastName ?? "",
            phone: person.phone ?? "",
            userUuid: person.userUuid ?? null,
            uuid: person.uuid ?? ev.db?.personUuid ?? null,
        };
    } else if (ev.db?.personUuid) {
        out.person = { uuid: ev.db.personUuid };
    }

    // ---- attendees (supports either your joined “Attendees” include or a plain array) ----
    const attendeesRaw =
        ev.Attendees ??
        ev.attendees ??
        ev.db?.attendees ??
        [];

    if (Array.isArray(attendeesRaw)) {
        out.attendees = attendeesRaw.map((a) => {
            // joined include shape in your SQL: Attendees->Attendee and Attendees->InvitedBy
            const attendeePerson = a.Attendee ?? a.Attendees?.Attendee ?? a.person ?? a.Person ?? null;
            const invitedBy = a.InvitedBy ?? a.Attendees?.InvitedBy ?? a.invitedBy ?? null;

            return {
                uuid: a.uuid ?? a.personUuid ?? attendeePerson?.uuid ?? null,
                status: a.status ?? "invited",
                role: a.role ?? "opt",
                mark: a.mark ?? "busy",

                firstName: attendeePerson?.firstName ?? a.firstName ?? "",
                lastName: attendeePerson?.lastName ?? a.lastName ?? "",
                phone: attendeePerson?.phone ?? a.phone ?? "",
                email: attendeePerson?.email ?? a.email ?? "",
                invitedBy: invitedBy
                    ? {
                        uuid: invitedBy.uuid ?? null,
                        displayName: invitedBy.displayName ?? "",
                        email: invitedBy.email ?? "",
                        firstName: invitedBy.firstName ?? "",
                        lastName: invitedBy.lastName ?? "",
                        phone: invitedBy.phone ?? "",
                        userUuid: invitedBy.userUuid ?? null,
                    }
                    : null,
            };
        });
    }
    
    out.timezones = (out.startsAt || out.endsAt)
        ? US_IANA_TIMEZONES.map((tz) => ({
            commonName: tz,
            startsAt: formatUtcIsoInTimeZone(out.startsAt, tz),
            endsAt: formatUtcIsoInTimeZone(out.endsAt, tz),
        }))
        : [];
    
    return out;
}

export const item = ({ uuid }) => async (dispatch) => {
    try {
        const res = await api.get(`/calendars/events/i/${uuid}`);
        const item = res.data?.items?.[0] ?? null;
        return item
            ? { ...item, description: normalizeCalendarDescription(item.description) }
            : null;
    } catch (err) {
        if (err?.response?.status === 401) dispatch({ type: LOGOUT });
        throw err;
    }
}

export const mapped = ({ uuid }) => async (dispatch) => {
    try {
        const data = await dispatch(item({ uuid }));
        return data ? mapApiEventToUi(data) : null;
    } catch (err) {
        if (err?.response?.status === 401) dispatch({ type: LOGOUT });
        throw err;
    }
}

export const send = ({ data: _data, uuid, email }) => async (dispatch) => {
    try {
        const data = _data ?? await dispatch(mapped({ uuid }));

        const attachments = [];
        if (data?.ics) {
            attachments.push({
                filename: `${(data?.title || "event").replace(/[^\w\-]+/g, "_")}.ics`,
                content: data.ics,
                contentType: "text/calendar; charset=utf-8",
            });
        }

        await dispatch(sendEmail({
            name: "EVENT.INVITE",
            json: data,
            recipient: email,
            attachments
        }));

        return data ?? {};
    } catch (err) {
        if (err?.response?.status === 401) dispatch({ type: LOGOUT });
        throw err;
    }
}

export const list = ({ startAt, endAt, calendar, history, before, limit, offset }) => async (dispatch) => {
    try {
        const query = new URLSearchParams();
        if (startAt) query.set("start", startAt);
        if (endAt) query.set("end", endAt);
        if (history) query.set("history", "true");
        if (before) query.set("before", before);
        if (limit !== undefined && limit !== null) query.set("limit", limit);
        if (offset !== undefined && offset !== null) query.set("offset", offset);

        const qs = query.toString();
        const res = await api.get(`/calendars/events/c/${calendar}${qs ? `?${qs}` : ""}`);
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) dispatch({ type: LOGOUT });
        throw err;
    }
}

export const save = ({
    uuid,
    sourceUuid,
    calendarUuid,
    personUuid,
    caseUuid,
    createdByUuid,
    status,
    style,
    eventType,
    title,
    description,
    location,
    startsAt,
    endsAt,
    allDay,
    recurrence,
    invites
}) => async (dispatch) => {
    try {
        const res = await api.post(`/calendars/events`, {
            uuid,
            sourceUuid,
            calendarUuid,
            personUuid,
            caseUuid,
            createdByUuid,
            status,
            style,
            eventType,
            title,
            description: normalizeCalendarDescription(description),
            location,
            startsAt,
            endsAt,
            allDay,
            recurrence,
            invites
        });
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) dispatch({ type: LOGOUT });
        throw err;
    }
}

export const destroy = ({ uuid }) => async (dispatch) => {
    try {
        const res = await api.delete(`/calendars/events/i/${uuid}`);
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) dispatch({ type: LOGOUT });
        throw err;
    }
}
